<template>
  <!-- The stridy.com site footer, at the bottom of the scrolling page (inside
       <v-main>, after the router view — see App.vue). Dark green in both themes,
       like the live site. -->
  <footer class="site-footer">
    <div class="site-footer__inner">
      <!-- Row 1: wordmark, with the site's rule running out to the right of it. -->
      <div class="footer-brand">
        <a class="footer-brand__logo" :href="brandHref" target="_top">
          <img
            :src="logoSrc"
            :srcset="logoSrcset"
            sizes="220px"
            alt="Stridy"
            loading="lazy"
          />
        </a>
        <span class="footer-brand__rule" aria-hidden="true"></span>
      </div>

      <!-- Row 2: the four link columns. -->
      <nav class="footer-links" aria-label="Stridy site footer">
        <div
          v-for="column in linkColumns"
          :key="column.heading"
          class="footer-links__col"
        >
          <h2 class="footer-links__heading">{{ column.heading }}</h2>
          <ul class="footer-links__list">
            <li v-for="link in column.links" :key="link.label">
              <a :href="link.href" target="_top">{{ link.label }}</a>
            </li>
          </ul>
        </div>
      </nav>

      <!-- Row 3: socials + contact on the left, store badges on the right. -->
      <div class="footer-meta">
        <div class="footer-meta__contact">
          <ul class="footer-socials">
            <li v-for="social in socials" :key="social.label">
              <a
                :href="social.href"
                target="_blank"
                rel="noopener"
                :aria-label="social.label"
              >
                <v-icon size="18">{{ social.icon }}</v-icon>
              </a>
            </li>
          </ul>
          <address class="footer-address">
            <span v-for="line in addressLines" :key="line">{{ line }}</span>
            <a class="footer-address__email" :href="`mailto:${email}`">
              {{ email }}
            </a>
          </address>
        </div>

        <ul class="footer-badges">
          <li v-for="badge in appBadges" :key="badge.label">
            <a :href="badge.href" target="_blank" rel="noopener">
              <img
                :src="badge.src"
                :srcset="badge.srcset"
                sizes="150px"
                :alt="badge.label"
                loading="lazy"
              />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script>
import {
  FOOTER_ADDRESS_LINES,
  FOOTER_APP_BADGES,
  FOOTER_BRAND_HREF,
  FOOTER_EMAIL,
  FOOTER_LINK_COLUMNS,
  FOOTER_LOGO_SRC,
  FOOTER_LOGO_SRCSET,
  FOOTER_SOCIALS,
} from "@/constants/siteFooter";

export default {
  name: "SiteFooter",

  data: () => ({
    brandHref: FOOTER_BRAND_HREF,
    logoSrc: FOOTER_LOGO_SRC,
    logoSrcset: FOOTER_LOGO_SRCSET,
    linkColumns: FOOTER_LINK_COLUMNS,
    socials: FOOTER_SOCIALS,
    addressLines: FOOTER_ADDRESS_LINES,
    email: FOOTER_EMAIL,
    appBadges: FOOTER_APP_BADGES,
  }),
};
</script>

<style lang="scss" scoped>
@import "@/styles/mixins.scss";

// The footer keeps the site's dark green in both themes (like the site-nav row
// keeps the brand lime), so its colours are constants here rather than the
// theme-flipping palette aliases.
$footer-bg: #0d281c;
$footer-fg: #ffffff;
// Links/contact text sit a touch quieter than the headings.
$footer-fg-muted: rgba(255, 255, 255, 0.78);
$footer-rule: rgba(255, 255, 255, 0.85);

// Widest the footer content stretches before it centres in the viewport, matching
// the live site's boxed sections.
$footer-max-width: 1140px;
// Vertical space between the three rows (brand / links / socials + badges).
$footer-row-gap: 44px;
// Gutter, matching .header-inner and .site-header__inner in the header.
$footer-gutter: 25px;
$footer-gutter-mobile: 16px;

.site-footer {
  width: 100%;
  background-color: $footer-bg;
  color: $footer-fg;
}

.site-footer__inner {
  display: flex;
  flex-direction: column;
  row-gap: $footer-row-gap;
  max-width: $footer-max-width;
  margin: 0 auto;
  padding: 56px $footer-gutter 48px;

  @include mobile {
    row-gap: #{$footer-row-gap * 0.7};
    padding: 40px $footer-gutter-mobile 32px;
  }
}

.footer-brand {
  display: flex;
  align-items: center;
  column-gap: 32px;

  @include mobile {
    column-gap: 20px;
  }
}

.footer-brand__logo {
  flex: 0 0 auto;
  display: flex;
  align-items: center;

  img {
    display: block;
    // Width follows from the wordmark's 1024x211 aspect ratio.
    height: 34px;
    width: auto;

    @include mobile {
      height: 26px;
    }
  }
}

// The rule takes whatever width the wordmark leaves.
.footer-brand__rule {
  flex: 1 1 auto;
  height: 1px;
  background-color: $footer-rule;
}

// Four columns while there's room; two in the tablet band, one on mobile.
.footer-links {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 24px;
  row-gap: 32px;

  @include below-desktop {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include mobile {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 24px;
  }
}

.footer-links__heading {
  margin: 0 0 14px;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.25;
  color: $footer-fg;
}

.footer-links__list {
  list-style: none;
  padding: 0;
  margin: 0;

  li + li {
    margin-top: 10px;
  }

  a {
    font-size: 15px;
    line-height: 1.4;
    color: $footer-fg-muted;
    text-decoration: none;

    &:hover,
    &:focus-visible {
      color: $footer-fg;
      text-decoration: underline;
    }
  }
}

.footer-meta {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  column-gap: 32px;
  row-gap: 32px;
  flex-wrap: wrap;
}

.footer-meta__contact {
  flex: 0 1 auto;
}

.footer-socials {
  display: flex;
  align-items: center;
  column-gap: 10px;
  list-style: none;
  padding: 0;
  margin: 0 0 22px;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.14);
    text-decoration: none;
    transition: background-color 0.2s ease;

    &:hover,
    &:focus-visible {
      background-color: rgba(255, 255, 255, 0.28);
    }
  }

  .v-icon {
    color: $footer-fg !important;
  }
}

.footer-address {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  font-size: 15px;
  font-style: normal;
  line-height: 1.5;
  color: $footer-fg-muted;
}

.footer-address__email {
  margin-top: 12px;
  color: $footer-fg-muted;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: $footer-fg;
    text-decoration: underline;
  }
}

.footer-badges {
  display: flex;
  align-items: center;
  column-gap: 20px;
  row-gap: 12px;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;

  img {
    display: block;
    // Height follows from each badge's own aspect ratio; the two artworks have
    // slightly different padding, so match on width instead.
    width: 150px;
    height: auto;

    @include mobile {
      width: 128px;
    }
  }
}
</style>
